import {
  createRouter,
  createWebHashHistory,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';
import HomeView from './views/HomeView.vue';
import LoginView from '@/views/LoginView.vue';
import CategoriesView from '@/views/CategoriesView.vue';
import UsersView from '@/views/UsersView.vue';
import StoriesView from '@/views/StoriesView.vue';
import QuizOfTheDayView from '@/views/QuizOfTheDayView.vue';
import StoryView from '@/views/StoryView.vue';
import Co2QuizView from '@/views/Co2QuizView.vue';
import ContentView from '@/views/ContentView.vue';
import InsightView from '@/views/InsightView.vue';
import LearningView from '@/views/LearningView.vue';
import EventsView from '@/views/EventsView.vue';
import LabelsView from '@/views/LabelsView.vue';
import StatisticsView from '@/views/StatisticsView.vue';
import TranslationsView from '@/views/TranslationsView.vue';
import StatisticsChartView from '@/views/StatisticsChartView.vue';
import InitiativeEditView from '@/views/InitiativeEditView.vue';
import InitiativeListView from '@/views/InitiativeListView.vue';
import LocationsView from '@/views/LocationsView.vue';

import store, { Actions, Mutations } from '@/store';
import RegisterView from './views/RegisterView.vue';
import BPTView from '@/views/BPTView.vue';
import FeedbackView from '@/views/FeedbackView.vue';
import DataTablesView from '@/views/DataTablesView.vue';
import MunicipalitiesView from '@/views/MunicipalitiesView.vue';
import { UserLevel } from './models/user.model';
import GoalsView from './views/GoalsView.vue';

const authenticateAdmin = async (
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: (r?: unknown) => unknown,
): Promise<void> => {
  store.commit(Mutations.GetState);
  await store.dispatch(Actions.GetCurrentUser);
  const user = store.state.loggedInUser;

  if (user && user.level === UserLevel.ContentEditor) {
    next({
      path: '/initiative/list',
      props: { redirect: to.fullPath },
    });
    return;
  }
  if (user && user.level === UserLevel.Admin) {
    next();
    return;
  }
  next({
    path: '/login',
    props: { redirect: to.fullPath },
  });
};

const authenticateContentEditor = async (
  to: RouteLocationNormalized,
  _: RouteLocationNormalized,
  next: (r?: unknown) => unknown,
): Promise<void> => {
  store.commit(Mutations.GetState);
  await store.dispatch(Actions.GetCurrentUser);
  const user = store.state.loggedInUser;

  if (user && [UserLevel.ContentEditor, UserLevel.Admin].includes(user.level)) {
    next();
    return;
  }
  next({
    path: '/login',
    props: { redirect: to.fullPath },
  });
};

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    meta: {
      title: 'Home',
    },
    component: HomeView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/login',
    name: 'login',
    meta: {
      title: 'Login',
    },
    component: LoginView,
  },
  {
    path: '/register',
    name: 'register',
    meta: {
      title: 'Register',
    },
    component: RegisterView,
  },
  {
    path: '/categories',
    name: 'categories',
    meta: {
      title: 'Categories',
    },
    component: CategoriesView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/users',
    name: 'users',
    meta: {
      title: 'Users',
    },
    component: UsersView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/users/communities',
    name: 'communities',
    meta: {
      title: 'Communities',
    },
    component: UsersView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/users/organizations',
    name: 'organizations',
    meta: {
      title: 'Organizations',
    },
    component: UsersView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/locations',
    name: 'locations',
    meta: {
      title: 'Locations',
    },
    component: LocationsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/stories',
    name: 'stories',
    meta: {
      title: 'Stories',
    },
    component: StoriesView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/quiz-of-the-day',
    name: 'quiz-of-the-day',
    meta: {
      title: 'Quiz of-the day',
    },
    component: QuizOfTheDayView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/stories/:id',
    name: 'story',
    meta: {
      title: 'Story',
    },
    component: StoryView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/co2quiz',
    name: 'co2quiz',
    meta: {
      title: 'CO₂ Quiz',
    },
    component: Co2QuizView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/co2quiz/values',
    name: 'co2values',
    meta: {
      title: 'CO₂ Values',
    },
    component: Co2QuizView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/co2quiz/averages',
    name: 'co2averages',
    meta: {
      title: 'CO₂ Averages',
    },
    component: Co2QuizView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/bpt',
    name: 'bpt',
    meta: {
      title: 'Behavior & Profile Tracker',
    },
    component: BPTView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/insight',
    name: 'insight',
    meta: {
      title: 'Insight',
    },
    component: InsightView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/content',
    name: 'content',
    meta: {
      title: 'Content',
    },
    component: ContentView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/learning',
    name: 'learning',
    meta: {
      title: 'Learning',
    },
    component: LearningView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/goals',
    name: 'goals',
    meta: {
      title: 'Goals',
    },
    component: GoalsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/events',
    name: 'events',
    meta: {
      title: 'Events',
    },
    component: EventsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/events/sources',
    name: 'sources',
    meta: {
      title: 'Events Sources',
    },
    component: EventsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/content/schedule',
    name: 'schedules',
    meta: {
      title: 'Schedules',
    },
    component: ContentView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/content/discounts',
    name: 'discounts',
    meta: {
      title: 'Discounts',
    },
    component: ContentView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/labels',
    name: 'labels',
    meta: {
      title: 'Labels',
    },
    component: LabelsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/data-tables',
    name: 'data-tables',
    meta: {
      title: 'Data Tables',
    },
    component: DataTablesView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/municipalities',
    name: 'municipalities',
    meta: {
      title: 'Municipalities',
    },
    component: MunicipalitiesView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/statistics',
    name: 'statistics',
    meta: {
      title: 'Statistics',
    },
    component: StatisticsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/translations',
    name: 'translations',
    meta: {
      title: 'Translations',
    },
    component: TranslationsView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/feedback',
    name: 'feedback-page',
    meta: {
      title: 'Feedback',
    },
    component: FeedbackView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/statistics/:slug',
    name: 'statistics-page',
    meta: {
      title: 'Statistics',
    },
    component: StatisticsChartView,
    beforeEnter: authenticateAdmin,
  },
  {
    path: '/initiative/list',
    name: 'list-initiative-page',
    meta: {
      title: 'Initiative List',
    },
    component: InitiativeListView,
    beforeEnter: authenticateContentEditor,
  },
  {
    path: '/initiative/:uuid/edit',
    name: 'public-initiative-page',
    meta: {
      title: 'Edit Initiative',
    },
    component: InitiativeEditView,
  },
];

const router = createRouter({
  history: createWebHashHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to) => {
  const { title } = to.meta;
  const defaultTitle = 'Shift CMS';

  document.title = `${defaultTitle} - ${title}` || defaultTitle;
});

export default router;
